import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { KpiCard } from '../components/teamkpis/KpiCard';
import { KpiSettingsDialog } from '../components/teamkpis/KpiSettingsDialog';
import { Loader2, Target, ChevronLeft, ChevronRight, Settings2, Users, Phone, PenLine, Search, Briefcase } from 'lucide-react';

const SECTION_ICON = { team: Users, max: PenLine, luis: PenLine, thomas: Search, talu: Briefcase };
// Talu, 29.10.2026: team section = 2×2 letter cards (finalised/sent, break-ups finalised/sent) above the remaining three.
const LETTER_KEYS = ['team_letters_finalized', 'team_letters_sent', 'team_breakups_written', 'team_breakups_sent'];

// KPI dashboard v2 (Talu, 28.10.2026): rolling per-KPI windows, pace over working hours, A/B/C split, Talu's broker KPIs.
export default function TeamKpisPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = useCallback(async (off) => {
    setLoading(true);
    try { setData((await api.getTeamKpis(off)).data); }
    catch (err) { toast.error(err.response?.data?.detail || 'KPIs konnten nicht geladen werden'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(offset); }, [offset, load]);

  const setWindowTarget = async (card, value) => {
    try {
      const r = await api.saveKpiConfig({ kpis: { [card.key]: { window_target: { deadline_date: card.window.deadline_date, value: value === '' ? null : Number(value) } } }, week_offset: offset });
      setData(r.data);
      toast.success(`Ziel für dieses Fenster gesetzt: ${value === '' ? 'Standard' : value}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Ziel konnte nicht gespeichert werden'); }
  };

  const renderCard = (card) => <KpiCard key={card.key} card={card} offset={offset} isAdmin={isAdmin} onSetWindowTarget={setWindowTarget} />;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-5" data-testid="team-kpis-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-heading text-foreground flex items-center gap-2"><Target className="w-5 h-5 text-sky-400" /> Team KPIs</h1>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Jede KPI hat ihr eigenes rollierendes Zählfenster bis zu ihrer Deadline. Soll-Linie steigt nur in der Arbeitszeit
            {data?.config?.work_hours ? ` (${data.config.work_hours.start}–${data.config.work_hours.end} Uhr)` : ''}. Zeiten: Europe/Berlin.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 border border-border rounded-sm px-1 py-0.5" data-testid="kpi-window-nav">
            <button onClick={() => setOffset((o) => Math.max(-52, o - 1))} className="p-1 text-muted-foreground hover:text-foreground" data-testid="kpi-window-prev"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-[11px] font-mono text-foreground min-w-[150px] text-center" data-testid="kpi-window-label">
              {data?.week?.label || '…'}{offset === 0 ? ' · aktuell' : ` · ${offset} Wo.`}
            </span>
            <button onClick={() => setOffset((o) => Math.min(0, o + 1))} disabled={offset === 0} className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30" data-testid="kpi-window-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} className="rounded-sm text-xs h-7" data-testid="kpi-settings-open">
              <Settings2 className="w-3.5 h-3.5 mr-1" /> Einstellungen
            </Button>
          )}
        </div>
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : data ? (
        <div className={`space-y-6 ${loading ? 'opacity-60' : ''}`}>
          {data.sections.map((sec) => {
            const Icon = SECTION_ICON[sec.key] || Users;
            return (
              <section key={sec.key} data-testid={`kpi-section-${sec.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-4 h-4 text-sky-400" />
                  <h2 className="text-base font-heading text-foreground">{sec.label}{sec.name && sec.key !== 'team' ? <span className="text-muted-foreground text-xs ml-2">{sec.name}</span> : null}</h2>
                </div>
                {sec.key === 'team' ? (
                  <>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4" data-testid="kpi-team-letters-grid">
                      {sec.cards.filter((c) => LETTER_KEYS.includes(c.key)).map(renderCard)}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mt-3 sm:mt-4" data-testid="kpi-team-other-grid">
                      {sec.cards.filter((c) => !LETTER_KEYS.includes(c.key)).map(renderCard)}
                    </div>
                  </>
                ) : (
                  <div className={`grid grid-cols-2 gap-3 sm:gap-4 ${sec.cards.length >= 3 ? 'xl:grid-cols-3' : ''}`}>
                    {sec.cards.map(renderCard)}
                  </div>
                )}
              </section>
            );
          })}
          {!data.people?.thomas && (
            <p className="text-[10px] text-muted-foreground" data-testid="kpi-thomas-missing">Sektion „Thomas“ erscheint, sobald der Nutzer thomas@olta-nachfolge.com angelegt ist.</p>
          )}
          {isAdmin && <KpiSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} onSaved={setData} weekOffset={offset} />}
          {isAdmin && (
            <p className="text-[10px] text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" /> Follow-up-Calls und Break-ups bekommen ihr Ziel automatisch aus den Sequenzen.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
